import editorialStyle from "../../../components/editorial/style.scss";
import style from "./style.scss";

import { Fragment } from "preact";
import Link, {
  Button,
  ButtonList,
  LargeViewOnly,
  StickyButtons,
  ButtonBar,
  affiliateMerchants,
} from "../../../components/link";
import AffiliateDisclosure from "../../../components/affiliate-disclosure";
import Center from "../../../components/center";
import Editorial from "../../../components/editorial";
import FixedTable from "../../../components/fixed-table";
import Head from "../../../components/head";
import Hero from "../../../components/hero";
import ImageSet from "../../../components/image-set";
import Image from "../../../components/image";
import Author from "../../../components/author";
import BluettiCouponCode from "../components/coupon-code";
import NoWrap from "../../../components/no-wrap";
import SlugHeading from "../../../components/slug-heading";
import SlugLink from "../../../components/slug-link";
import Accordion from "../../../components/accordion";
import YouTube from "../../../components/youtube";
import Warning from "../../../components/warning";
import AccordionPreview from "../../../components/accordion/preview";
import PhotoListItem from "../../../components/photo-list-item";
import Tags from "../../../components/tags";
import ReviewItem from "../../../components/review-item";
import Questions from "../../../components/questions";
import namedRoutes from "../../../settings/routes";
import { lookupByUrl } from "../../../settings/route-data-lookup";

let headerInfo = lookupByUrl(namedRoutes.bluetti.charger2.review);

const price = {
  current: 499,
  msrp: 599,
};

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      positionX={25}
      positionY={73}
      background="/images/products/bluetti/charger-2/background.webp"
      titlePrefix="BLUETTI"
      title="Charger 2 Review"
      subTitle="1200W Alternator & Solar Charging"
    />
    <Editorial>
      <Center>
        <Author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <LargeViewOnly>
          <ButtonList size="medium">
            <Button
              size="medium"
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
              icon="Bluetti"
            >
              View on BLUETTI
            </Button>
            <Button
              size="medium"
              affiliate="https://amzn.to/4q6J9LD"
              href="https://www.amazon.com/dp/B0FS7C11QG/"
              secondary
              icon="Amazon"
            >
              View on Amazon
            </Button>
          </ButtonList>
        </LargeViewOnly>

        <p>
          The <strong>BLUETTI Charger 2</strong> is an all-new DC-DC alternator
          charger designed to push the boundaries of vanlife, RV, and off‑grid
          power systems. In this review, I break down the capabilities, new
          features, and important considerations to help you decide if it’s the
          right fit for your setup. It offers several advanced capabilities,
          including up to 1,200W combined output and the ability to
          reverse-charge your vehicle's starter battery. However, taking full
          advantage of these features requires compatible hardware and a clear
          understanding of the system’s limitations.
        </p>
        <p>
          This review covers everything you need to know about Charger 2. I
          explain how it works, what is new, where it performs well and where it
          falls short.
        </p>

        <AffiliateDisclosure />

        {/* <ButtonList size="medium">
          <Button
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/pages/charger-2-upgrade-campaign"
            icon="Bluetti"
          >
            Upgrade Bonus Info
          </Button>
        </ButtonList> */}

        <ImageSet
          size={4}
          shadow
          images={[
            {
              lg: "/images/products/bluetti/charger-2/1920/charger-2_9445.webp",
              sm: "/images/products/bluetti/charger-2/500/charger-2_9445.webp",
              alt: "BLUETTI Charger 2: cables plugged in",
            },
            {
              lg: "/images/products/bluetti/charger-2/1920/charger-2_9458.webp",
              sm: "/images/products/bluetti/charger-2/500/charger-2_9458.webp",
              alt: "BLUETTI Charger 2: bottom ports",
            },
            {
              lg: "/images/products/bluetti/charger-2/1920/charger-2_9450.webp",
              sm: "/images/products/bluetti/charger-2/500/charger-2_9450.webp",
              alt: "BLUETTI Charger 2",
            },
            {
              lg: "/images/products/bluetti/charger-2/1920/charger-2_9463.webp",
              sm: "/images/products/bluetti/charger-2/500/charger-2_9463.webp",
              alt: "BLUETTI Charger 2: accessories included in package (unboxing)",
            },

            {
              lg: "/images/products/bluetti/charger-2/1920/charger-2_dc-hub_9455.webp",
              sm: "/images/products/bluetti/charger-2/500/charger-2_dc-hub_9455.webp",
              alt: "BLUETTI Charger 2 and DC Hub (Hub D1)",
            },
            {
              lg: "/images/products/bluetti/charger-2/1920/charger-2_9446.webp",
              sm: "/images/products/bluetti/charger-2/500/charger-2_9446.webp",
              alt: "BLUETTI Charger 2: cables plugged in, cover off",
            },
            {
              lg: "/images/products/bluetti/charger-2/1920/charger-2_9459.webp",
              sm: "/images/products/bluetti/charger-2/500/charger-2_9459.webp",
              alt: "BLUETTI Charger 2: bottom ports, cover off",
            },
            {
              lg: "/images/products/bluetti/charger-2/1920/charger-2_9448.webp",
              sm: "/images/products/bluetti/charger-2/500/charger-2_9448.webp",
              alt: "BLUETTI Charger 2: cover off",
            },
          ]}
        />

        <p class="caption-left">More photos and setup instructions below.</p>

        <Accordion
          items={[
            {
              heading: "Table of Contents",
              content: (
                <ol>
                  <li>
                    <SlugLink>Overview</SlugLink>
                  </li>
                  <li>
                    <SlugLink>What's New</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Considerations</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Detailed Comparison</SlugLink>
                    <ol class="upper-alpha">
                      <li>
                        <SlugLink>Full Specifications</SlugLink>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <SlugLink>How to Install</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Setup Videos</SlugLink>
                  </li>
                  <li>
                    <SlugLink>App Control</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Accessories</SlugLink>
                    <ol class="upper-alpha">
                      <li>
                        <SlugLink>DC Hub</SlugLink>
                      </li>
                      <li>
                        <SlugLink>DIY D+ Cable</SlugLink>
                      </li>
                      <li>
                        <SlugLink>Bidirectional Compatibility</SlugLink>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <SlugLink>My Verdict</SlugLink>
                  </li>
                </ol>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Overview</SlugHeading>
        <ul>
          <li>
            <strong>Automatic Charging:</strong> Automatically controls charging
            using intelligent voltage detection or an optional ignition wire so
            it only operates when the vehicle is running.
          </li>
          <li>
            <strong>Multifunctional:</strong> In addition to charging, when
            connected to a compatible BLUETTI power station it also offers
            reverse charging and battery maintenance for the vehicle's battery.
          </li>
          <li>
            <strong>Broad Compatibility:</strong> Basic charging and solar also
            work with many third‑party models from brands like EcoFlow and
            Jackery. This flexibility makes it a versatile option for
            mixed‑brand power setups.
          </li>
          <li>
            <strong>Smart App Control:</strong> Monitor energy flow and adjust
            charging settings via the BLUETTI app.
          </li>
          <li>
            <strong>Backwards Compatible Wiring:</strong> Designed as a true
            drop‑in upgrade for Charger 1 users. Existing wiring can stay in
            place, which makes upgrading fast, clean, and hassle‑free.
          </li>
          <li>
            <strong>Configurable Charging Power:</strong> When using the power
            station port (CHG), you can manually throttle the wattage to a
            desired level, making it compatible with smaller alternators or
            specific batteries.
          </li>
        </ul>
      </Center>
      <Center>
        <SlugHeading tag="h2">What's New</SlugHeading>
        <ul>
          <li>
            <strong>Increase in Charging Power:</strong> Delivers a
            significantly higher charging rate compared to the previous
            generation, supporting up to <strong>800W</strong> of input from the
            alternator (at 12V or 24V) for a combined total charging power of up
            to 1,200W maximum when paired with solar and using power station
            port (CHG).
          </li>
          <li>
            <strong>Bidirectional Port:</strong> A bidirectional port (BAT)
            designed for connecting a BLUETTI power station with an expansion
            port (
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/apex-300-home-battery-backup?id=68804e7e63e18b529ef2604a&variant=47351562174683"
            >
              Apex 300
            </Link>
            ,{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/ac200l?id=673d48af5295be4bd97b0955&variant=46352232349915"
            >
              AC200L
            </Link>
            ,{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/ac200max-power-station?variant=44272419995867"
            >
              AC200MAX
            </Link>
            ) or a BLUETTI expansion battery (
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/b300k?id=673c0fb87410b96647e90c63&variant=45943077208283"
            >
              B300K
            </Link>
            ,{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/b500k-expansion-battery?id=69118a354abc44066f33eb5b&variant=47914931847387"
            >
              B500K
            </Link>
            ,{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/b230-battery-pack"
            >
              B230
            </Link>
            ,{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/b300"
            >
              B300
            </Link>
            ). Charge at up to 1,000W maximum when paired with solar.
          </li>
          <li>
            <strong>Solar Input:</strong> Now features a dedicated{" "}
            <strong>600W</strong> solar input (PV) with a voltage range of
            13-50V. This allows you to charge your power station from solar
            panels while stationary or supplement the alternator while driving.
            This is a major improvement for power stations that only have a
            single PV input to share between solar and alternator charging.
          </li>
          <li>
            <strong>DC Hub Accessory:</strong> Expand into a complete DC power
            distribution center by adding the{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/dc-hub-for-charger-2"
            >
              DC Hub
            </Link>
            . This provides nine extra DC output ports, including a 50A Anderson
            SB50 port, cigarette lighter, USB-C, USB-A, and DC5521. Requires a
            compatible BLUETTI device connected to the bidirectional port to be
            active when the vehicle is off.
          </li>
          <li>
            <strong>Two Power Stations:</strong> Supports charging two power
            stations simultaneously: one through the power station port (CHG)
            and the other through the bidirectional port (BAT).
          </li>
          <li>
            <strong>Ignition‑Triggered Auto Start:</strong> Can be configured to
            automatically power on via the vehicle's ignition or engine run
            signals using an optional{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/charger-2-d-signal-wire"
            >
              D+ signal wire
            </Link>{" "}
            accessory. This is a major improvement, especially for vehicles with
            smart alternators, as it ensures the charger only draws power when
            the engine is running and continues to charge even when the voltage
            decreases.
          </li>
        </ul>
      </Center>
      <Center>
        <SlugHeading tag="h2">Considerations</SlugHeading>
        <ul>
          <li>
            <strong>Price:</strong> Currently one of the{" "}
            <Link href={namedRoutes.alternatorChargers}>
              most expensive options
            </Link>{" "}
            on the market, but does offer several advanced features that many
            other models don't include.
          </li>
          <li>
            <strong>Power Station Compatibility:</strong> Third-party power
            stations <strong>and BLUETTI models</strong> without a compatible
            expansion port are restricted to the one-way power station port.
            These units can be charged but do not support reverse charging
            features or the ability to power the{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/dc-hub-for-charger-2"
            >
              DC Hub
            </Link>
            .
          </li>
          {/* <li>
            <strong>Expansion Battery Limitation:</strong> Since most BLUETTI
            power stations do not have two expansion battery port, most rely on
            chaining but uses the same port as the how the power station would
            connect to the bidirectional port. A power station connected instead
            via the XT60 port cannot send its power to the DC Hub or be used by
            reverse charging. This means if you plan to power DC appliances such
            as a fride from the DC Hub then it only has access to power from the
            expansion battery not the power station power. The only BLUETTI
            power station with two expansion battery ports supported by the
            bidirectional port is the older AC200MAX.
          </li> */}
          <li>
            <strong>Lower Bidirectional Charge:</strong> Bidirectional port only
            supports 1,000W, whereas the power station port supports 1,200W.
          </li>
          <li>
            <strong>Jump‑Start Limitation:</strong> Charger 2 cannot be used to
            jump‑start a vehicle. Its reverse‑charge output is limited to 800W.
            While this is suitable for recharging a starter battery, it is far
            below the surge current required for engine starting. For instant
            jump-starting, consider the{" "}
            <Link
              affiliate="https://amzn.to/4jzrp9n"
              href="https://www.amazon.com/dp/B015TKUPIC/"
            >
              NOCO Boost GB40
            </Link>{" "}
            for standard vehicles or the{" "}
            <Link
              affiliate="https://amzn.to/3N7RO26"
              href="https://www.amazon.com/dp/B016UG6PWE/"
            >
              NOCO Boost GB70
            </Link>{" "}
            for larger trucks.
          </li>
          {/* <li>
            <strong>Accessories Unavailable:</strong> Some accessories are not
            yet available, and the D+ and FridgePower cables are not expected
            until June 2026.
          </li> */}
          <li>
            <strong>DC Hub Limitation:</strong> This port only provides power
            when the vehicle is running or when a compatible BLUETTI device is
            connected to the bidirectional port. It does not function with solar
            input alone or while the vehicle is off without a device connected
            to the bidirectional port.
          </li>
          <li>
            <strong>D+ Cable Not Included:</strong> If your vehicle uses a smart
            alternator (common on most models built after 2015), you’ll likely
            want the optional{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/charger-2-d-signal-wire"
            >
              D+ signal wire
            </Link>
            . Since it’s not included in the box, plan to purchase one
            separately for more reliable automatic-start behavior.
          </li>
          <li>
            <strong>Fan noise & performance:</strong>
            The fan was louder than expected, especially compared to brands that
            offer fanless chargers. This is less of a concern in practice
            because vehicles are noisy while driving. Charger 2 also offers a
            “Silent Charging” mode, which works by significantly throttling the
            charging rate (On my unit, it drops from 790W to 265W).
          </li>
          <li>
            <strong>Large Fuse:</strong> The included fuse is large and may not
            fit in some recessed battery compartments. To resolve this, you can
            use a separate extension cable with an inline fuse block and a
            75A-rated fuse.
          </li>
          <li>
            <strong>Limited Ingress Protection:</strong> With an IP20 rating,
            the Charger 2 offers no protection against water and minimal
            protection against dust. Keep this in mind if you plan to install it
            in dusty environments. Long-term dust buildup inside the unit could
            impact cooling performance compared to a sealed, fanless design.
          </li>
          <li>
            <strong>Altitude Limitation:</strong> The manual lists an operating
            altitude of up to 6,561 ft (2,000 m). If that limit is strict, it is
            a real constraint for people who camp or travel at higher
            elevations. BLUETTI should raise this limit in future hardware
            revisions or provide clearer guidance on what to expect above it.
          </li>
        </ul>

        <Warning icon={false}>
          <SlugHeading tag="h3">Issues</SlugHeading>
          <p>
            There are a few issues to be aware of with Charger 2 and the DC Hub.
            These may only surface in certain setups, but they are worth noting
            if you require specific functionality.
          </p>
          <Accordion
            items={[
              {
                heading: "Bluetooth Connectivity",
                content: (
                  <Fragment>
                    <p>
                      Charger 2 can enter a state where the unit is powered on
                      and functional, but the app cannot establish a Bluetooth
                      connection. In my testing, holding down the power button
                      for 15 seconds to force a restart did not resolve the
                      issue. The only way to restore the connection was to
                      physically disconnect and reconnect the cable from the ALT
                      port. This is a significant concern for installations
                      where cable access is limited.
                    </p>
                    <Warning blue icon={false} prefix="Workaround">
                      Ensure any device is disconnected from the P090D
                      connection (BAT), then disconnect and reconnect the cable
                      from the ALT port.
                    </Warning>
                  </Fragment>
                ),
              },
              {
                heading: "Priority Charging",
                content: (
                  <Fragment>
                    <p>
                      Charger 2 does not follow the "Priority Charging" app
                      setting when set to "Parking Battery." In testing with an
                      BLUETTI AC200L on the BAT port and an EcoFlow Delta 3
                      Classic on the CHG port, the unit continued to charge the
                      EcoFlow from the AC200L. This behavior should only occur
                      when "Priority Charging" is set to "PPS" (Portable Power
                      Station). It’s possible the AC200L is also at fault for
                      this issue, but I cannot confirm that since I do not have
                      access to other devices such as an Apex 300 or B300K for
                      testing.
                    </p>
                    <Warning blue icon={false} prefix="Workaround">
                      Manually unplug the power station or disable charging in
                      the power station’s settings if supported. Otherwise,
                      Charger 2 will continue transferring power until the power
                      station on the CHG port reaches full charge.
                    </Warning>
                  </Fragment>
                ),
              },
              {
                heading: "Power Station Sleep Mode",
                content: (
                  <Fragment>
                    <p>
                      The BLUETTI AC200L features an undocumented auto-shutdown
                      that triggers after three hours if the AC, DC, and USB
                      outputs are all disabled. When the unit powers down,
                      Charger 2 loses its source and enters sleep mode,
                      effectively killing power to the DC Hub. To bypass this,
                      you must either enable an output or maintain a significant
                      enough load on the DC Hub to keep the system active. I do
                      not have access to other devices such as an AC300 or B300K
                      for testing to determine if they exhibit the same
                      behavior.
                    </p>
                    <Warning blue icon={false} prefix="Workaround">
                      On the power station, keep DC or USB power enabled and
                      ensure the DC‑ECO setting is disabled in the app.
                    </Warning>
                  </Fragment>
                ),
              },
              {
                heading: "DC Hub System Switch Recovery",
                content: (
                  <Fragment>
                    <p>
                      The DC Hub does not follow its "System Switch Recovery"
                      setting. When set to "Enabled," the device should remember
                      whether the DC output was on or off. Instead, after
                      Charger 2 restarts, the DC Hub always defaults to the off
                      position.
                    </p>
                    <Warning blue icon={false} prefix="Workaround">
                      Manually turn the DC Hub back on each time it powers up.
                    </Warning>
                  </Fragment>
                ),
              },
              // {
              //   heading: "DC Hub Output Voltage",
              //   content: (
              //     <Fragment>
              //       <p>
              //         The "DC Output Voltage" setting for the DC Hub fails to
              //         load within the app. The output is 13.5V when on, but the
              //         setting field is blank. Tapping the field results in a
              //         loading spinner that never resolves, which is an issue if
              //         you need to use the advertised 24V mode.
              //       </p>
              //       <Warning blue icon={false} prefix="Workaround">
              //         Use a DC boost converter, such as the{" "}
              //         <Link
              //           affiliate="https://amzn.to/4tW6Xnd"
              //           href="https://www.amazon.com/dp/B0FGW7ZYV7/"
              //         >
              //           Daygreen 12V to 24V 25A 600W
              //         </Link>
              //         , to convert the 12V output to 24V from the Anderson port.
              //       </Warning>
              //     </Fragment>
              //   ),
              // },
            ]}
          />

          <p>
            I'm in direct communication with BLUETTI regarding these findings
            and will update this review as they are resolved. I have also
            started a{" "}
            <Link
              external
              href="https://www.reddit.com/r/bluetti/comments/1sp7fz8/bluetti_charger_2_dc_hub_issues_tracking/"
            >
              Reddit thread
            </Link>{" "}
            so others in the community can voice if they are impacted by these
            issues; feel free to join the discussion there.
          </p>
        </Warning>
      </Center>
      <Center>
        <SlugHeading tag="h2">Detailed Comparison</SlugHeading>
        <p>
          BLUETTI{" "}
          <strong>
            <NoWrap>Charger 2</NoWrap>
          </strong>{" "}
          vs{" "}
          <strong>
            <NoWrap>Charger 1</NoWrap>
          </strong>{" "}
          comparison table:
        </p>

        <Fragment>
          <FixedTable>
            <table>
              <thead>
                <tr>
                  <th class={editorialStyle.sticky}>Model</th>
                  <th>Charger 2</th>
                  <th>Charger 1</th>
                </tr>
              </thead>
              <tbody>
                <tr class={editorialStyle.noHover}>
                  <th class={editorialStyle.sticky}>
                    <strong>Photo</strong>
                  </th>
                  <td>
                    <Image
                      src="/images/products/bluetti/charger-2/front.webp"
                      alt="BLUETTI Charger 2"
                      showAlt
                      maxHeight={100}
                    />
                  </td>
                  <td>
                    <Image
                      src="/images/products/bluetti/charger-1/front.webp"
                      alt="BLUETTI Charger 1"
                      showAlt
                      maxHeight={100}
                    />
                  </td>
                </tr>
                <tr class={editorialStyle.noHover}>
                  <th class={editorialStyle.sticky}>
                    <strong>Links</strong>
                  </th>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
                        icon="Bluetti"
                      >
                        Store
                      </Button>
                      <Button
                        size="small"
                        external
                        href="https://cdn.shopify.com/s/files/1/0550/5620/3836/files/CHARGER_2_User_Manual_EN.pdf?v=1768545688"
                        secondary
                      >
                        Manual
                      </Button>
                    </ButtonBar>
                  </td>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/bluetti-charger-1"
                        icon="Bluetti"
                      >
                        Store
                      </Button>
                      <Button
                        size="small"
                        external
                        href="https://s4.bluettipower.com/bluetti_lgf/support/2025/05/4a4f1b84-aa5a-4563-8e3f-28f5c3053576.pdf"
                        secondary
                      >
                        Manual
                      </Button>
                    </ButtonBar>
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Price</strong>
                  </th>
                  <td>
                    <NoWrap>
                      ${price.current}{" "}
                      {price.current < price.msrp ? <s>${price.msrp}</s> : null}
                    </NoWrap>
                  </td>
                  <td>
                    $249 <s>$399</s>
                  </td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>Alternator Input</strong>
                  </th>
                  <td>800W</td>
                  <td>560W</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Solar Input</strong>
                  </th>
                  <td>13-50V⎓20A, 600W</td>
                  <td>--</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Power Station Port</strong>
                  </th>
                  <td>1,200W</td>
                  <td>560W</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Bidirectional Port</strong>
                  </th>
                  <td>
                    1,000W <sup>1</sup>
                  </td>
                  <td>--</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>DC Hub Port</strong>
                  </th>
                  <td>
                    700W <sup>2</sup>
                  </td>
                  <td>--</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Max Reverse Charge</strong>
                  </th>
                  <td>
                    800W <sup>1</sup>
                  </td>
                  <td>--</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Battery Maintenance</strong>
                  </th>
                  <td>
                    100W <sup>1</sup>
                  </td>
                  <td>--</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Signal Wire</strong> (D+)
                  </th>
                  <td>Add-on</td>
                  <td>--</td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>Size</strong>
                  </th>
                  <td>
                    10.4 × 6.7 × 2.7 in
                    <br />
                    (265 × 169 × 70 mm)
                  </td>
                  <td>
                    5.7 × 4.3 × 2.4 in
                    <br />
                    (145 × 110 × 60 mm)
                  </td>
                </tr>
              </tbody>
            </table>
          </FixedTable>
          <p class="caption">
            <strong>1.</strong> Requires compatible BLUETTI product.{" "}
            <strong>2.</strong> Requires BLUETTI{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/dc-hub-for-charger-2"
            >
              DC Hub
            </Link>
            . Also requires a BLUETTI device connected to the bidirectional port
            to be active when vehicle is off.
          </p>
        </Fragment>

        <p>
          Charger 2 is larger and more expensive than Charger 1, but it
          introduces several advanced charging features that are not possible
          with the base model.
        </p>
        <ImageSet
          size={2}
          shadow
          images={[
            {
              lg: "/images/products/bluetti/charger-2/1920/charger-2_9746.webp",
              sm: "/images/products/bluetti/charger-2/500/charger-2_9746.webp",
              alt: "BLUETTI Charger 2 vs Charger 1 size comparison",
            },
          ]}
        />

        <p>For more details about Charger 1 see my review:</p>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.bluetti.charger1.review} />
          </li>
        </ul>

        <p>
          Not everyone needs all of Charger 2’s advanced features. I would love
          to see BLUETTI offer a lower‑cost version that keeps the Charger 1
          model's core functionality while adding built‑in D+ support and an
          adjustable charging rate. That would better serve users who want
          reliable alternator charging, especially for vehicles with a smart
          alternator.
        </p>

        <SlugHeading tag="h3">Full Specifications</SlugHeading>
        <Accordion
          items={[
            {
              heading: "Specifications",
              content: (
                <Fragment>
                  <h4>General</h4>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Model:</strong> Charger 2
                    </li>
                    <li>
                      <strong>Net Weight:</strong> 3.51 lbs (1.59 kg)
                    </li>
                    <li>
                      <strong>Dimensions:</strong> 10.43 × 6.65 × 2.74 in (265 ×
                      169 × 69.7 mm)
                    </li>
                    <li>
                      <strong>Noise Level:</strong> 50 dB Max (app includes a
                      "Quiet Mode")
                    </li>
                    <li>
                      <strong>Protection Rating:</strong> IP20
                    </li>
                    <li>
                      <strong>Warranty Period:</strong> 2 years
                    </li>
                  </ul>
                  <hr />
                  <h4>Power</h4>
                  <ul class="none" style="font-size:0.9em;">
                    <li>
                      <strong>Car Battery Input/Output (ALT):</strong> 800W Max,
                      12.8V⎓62.5A, 25.6V⎓31.3A
                    </li>
                    <li>
                      <strong>Bidirectional Power Port (BAT):</strong> 1,000W
                      Max, 19.2V⎓52A, 25.6V⎓39A, 51.2V⎓19.5A
                    </li>
                    <li>
                      <strong>Solar Input (PV):</strong> 600W Max, 13-50V⎓20A
                    </li>
                    <li>
                      <strong>Power Station Output (CHG):</strong> 1,200W Max,
                      12-56V⎓24A
                    </li>
                    <li>
                      <strong>48V DC Hub Port (DC PANEL):</strong> 700W,
                      40-58.5V 12.5A
                    </li>
                    <li>
                      <strong>Fuse Rated Current:</strong> 75A
                    </li>
                    <li>
                      <strong>Protection Type:</strong> Reverse polarity, Over
                      current, Short circuit, Over voltage, Under voltage
                      protection
                    </li>
                  </ul>
                  <hr />
                  <h4>Environmental Temperature</h4>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Operating Temperature:</strong> -4°F to 140°F
                      (-20°C to 60°C), Power decreases above 104°F (40°C)
                    </li>
                    <li>
                      <strong>Storage Temperature:</strong> -4°F to 140°F (-20°C
                      to 60°C)
                    </li>
                    <li>
                      <strong>Operating Humidity:</strong> 10% to 90%
                    </li>
                    <li>
                      <strong>Operating Altitude:</strong> &lt; 6,561 ft (2,000
                      m)
                    </li>
                  </ul>
                  <hr />
                  <h4>Communications</h4>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Wi-Fi:</strong> Yes
                    </li>
                    <li>
                      <strong>Bluetooth:</strong> Yes
                    </li>
                  </ul>
                </Fragment>
              ),
            },
            {
              heading: "Cables",
              content: (
                <Fragment>
                  <h4>Standard Package</h4>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Car Battery Cables:</strong> 16.4 ft (5 m), 6 AWG,
                      with 75A fuse
                    </li>
                    <li>
                      <strong>MC4 Input Cable:</strong> 7.87 in (200 mm)
                    </li>
                    <li>
                      <strong>MC4 Output Cable:</strong> 7.87 in (200 mm)
                    </li>
                    <li>
                      <strong>Solar Charging Cable (MC4 to XT60):</strong> 7.87
                      in (200 mm)
                    </li>
                    <li>
                      <strong>Bidirectional Power Cable (P090D):</strong> 4.92
                      ft (1.5 m), for{" "}
                      <Link
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/apex-300-home-battery-backup?id=68804e7e63e18b529ef2604a&variant=47351562174683"
                      >
                        Apex 300
                      </Link>
                      ,{" "}
                      <Link
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/ac200l?id=673d48af5295be4bd97b0955&variant=46352232349915"
                      >
                        AC200L
                      </Link>
                      ,{" "}
                      <Link
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/ac200max-power-station?variant=44272419995867"
                      >
                        AC200MAX
                      </Link>
                      ,{" "}
                      <Link
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/b300k?id=673c0fb87410b96647e90c63&variant=45943077208283"
                      >
                        B300K
                      </Link>
                      ,{" "}
                      <Link
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/b500k-expansion-battery?id=69118a354abc44066f33eb5b&variant=47914931847387"
                      >
                        B500K
                      </Link>
                      ,{" "}
                      <Link
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/b230-battery-pack"
                      >
                        B230
                      </Link>
                      ,{" "}
                      <Link
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/b300"
                      >
                        B300
                      </Link>
                    </li>
                  </ul>
                  <hr />

                  <h4>Charger 1 Upgrade Package</h4>
                  <p>Includes all standard items except: Car Battery Cables</p>
                </Fragment>
              ),
            },
          ]}
        />

        <SlugHeading tag="h3">Check Compatibility</SlugHeading>
        <p>
          View my{" "}
          <Link href={namedRoutes.bluetti.charger2.compatibility}>
            detailed compatibility guide
          </Link>{" "}
          for {/* confirmed */} BLUETTI power station support and 3rd-party
          battery system limits. My guide outlines the exact wattage limits for
          every BLUETTI power station using Charger 2 alternator and/or solar
          charging. It also includes a <strong>compatibility calculator</strong>{" "}
          for 3rd-party brands like EcoFlow, Jackery, and Anker to ensure your
          setup is safe and efficient.
        </p>
        <ReviewItem href={namedRoutes.bluetti.charger2.compatibility} />
      </Center>
      <Center>
        <SlugHeading tag="h3">How to Install</SlugHeading>
        <p>
          Installing Charger 2 isn't difficult, but it does require vehicle
          battery access, making the DC electrical connections, thoughtful cable
          routing, and a few basic tools.
        </p>

        <p class="caption-left">Enlarge photos for captions.</p>
        <ImageSet
          size={4}
          maxShow={8}
          border={true}
          showAlt
          images={[
            {
              lg: "/images/products/bluetti/charger-2/install/1920/charger-2_install_9570.webp",
              sm: "/images/products/bluetti/charger-2/install/500/charger-2_install_9570.webp",
              alt: "Unscrew and remove the cover from Charger 2",
            },
            {
              lg: "/images/products/bluetti/charger-2/install/1920/charger-2_install_9576.webp",
              sm: "/images/products/bluetti/charger-2/install/500/charger-2_install_9576.webp",
              alt: "Connect battery cables to ALT ports",
            },
            {
              lg: "/images/products/bluetti/charger-2/install/1920/charger-2_install_9579.webp",
              sm: "/images/products/bluetti/charger-2/install/500/charger-2_install_9579.webp",
              alt: "Connect cables to CHG ports",
            },
            {
              lg: "/images/products/bluetti/charger-2/install/1920/charger-2_install_9582.webp",
              sm: "/images/products/bluetti/charger-2/install/500/charger-2_install_9582.webp",
              alt: "Connect cables to PV ports",
            },

            {
              lg: "/images/products/bluetti/charger-2/install/1920/charger-2_install_9587.webp",
              sm: "/images/products/bluetti/charger-2/install/500/charger-2_install_9587.webp",
              alt: "Connect cables to DC Panel ports",
            },
            {
              lg: "/images/products/bluetti/charger-2/install/1920/charger-2_install_9593.webp",
              sm: "/images/products/bluetti/charger-2/install/500/charger-2_install_9593.webp",
              alt: "Connect cables to BAT ports",
            },
            {
              lg: "/images/products/bluetti/charger-2/install/1920/charger-2_install_9597.webp",
              sm: "/images/products/bluetti/charger-2/install/500/charger-2_install_9597.webp",
              alt: "Connect cable to D+ port",
            },
            {
              lg: "/images/products/bluetti/charger-2/install/1920/charger-2_install_9601.webp",
              sm: "/images/products/bluetti/charger-2/install/500/charger-2_install_9601.webp",
              alt: "Reinstall the cover on Charger 2",
            },

            {
              lg: "/images/products/bluetti/charger-2/install/1920/charger-2_install_9604.webp",
              sm: "/images/products/bluetti/charger-2/install/500/charger-2_install_9604.webp",
              alt: "Connect the power station's solar charging cable to Charger 2",
            },
            {
              lg: "/images/products/bluetti/charger-2/install/1920/charger-2_install_9625.webp",
              sm: "/images/products/bluetti/charger-2/install/500/charger-2_install_9625.webp",
              alt: "Connect a portable power station",
            },
            {
              lg: "/images/products/bluetti/charger-2/install/1920/charger-2_install_9657.webp",
              sm: "/images/products/bluetti/charger-2/install/500/charger-2_install_9657.webp",
              alt: "Connect solar panels to Charger 2",
            },

            {
              lg: "/images/products/bluetti/charger-2/install/1920/charger-2_install_9617.webp",
              sm: "/images/products/bluetti/charger-2/install/500/charger-2_install_9617.webp",
              alt: "Connect BLUETTI power station or expansion battery",
            },

            {
              lg: "/images/products/bluetti/charger-2/install/1920/charger-2_install_9663.webp",
              sm: "/images/products/bluetti/charger-2/install/500/charger-2_install_9663.webp",
              alt: "Connect to vehicle battery",
            },
            {
              lg: "/images/products/bluetti/charger-2/install/1920/charger-2_install_9647_2.webp",
              sm: "/images/products/bluetti/charger-2/install/500/charger-2_install_9647_2.webp",
              alt: "Setup Charger 2 from BLUETTI app",
            },
          ]}
        />

        {/* <ImageSet
          size={4}
          border={true}
          showAlt
          images={[
            {
              lg: "/images/products/bluetti/charger-2/lg/install-1.webp",
              sm: "/images/products/bluetti/charger-2/sm/install-1.webp",
              alt: "Unscrew and remove the cover from Charger 2",
            },
            {
              lg: "/images/products/bluetti/charger-2/lg/install-2.webp",
              sm: "/images/products/bluetti/charger-2/sm/install-2.webp",
              alt: "Connect required cables to Charger 2",
            },
            {
              lg: "/images/products/bluetti/charger-2/lg/install-3.webp",
              sm: "/images/products/bluetti/charger-2/sm/install-3.webp",
              alt: "Check and secure all the connections to Charger 2",
            },
            {
              lg: "/images/products/bluetti/charger-2/lg/install-4.webp",
              sm: "/images/products/bluetti/charger-2/sm/install-4.webp",
              alt: "Connect the power station's solar charging cable and solar panels to Charger 2",
            },
            {
              lg: "/images/products/bluetti/charger-2/lg/install-5.webp",
              sm: "/images/products/bluetti/charger-2/sm/install-5.webp",
              alt: "Connect to the power station",
            },
            {
              lg: "/images/products/bluetti/charger-2/lg/install-6.webp",
              sm: "/images/products/bluetti/charger-2/sm/install-6.webp",
              alt: "Add power station or expansion battery",
            },
            {
              lg: "/images/products/bluetti/charger-2/lg/install-7.webp",
              sm: "/images/products/bluetti/charger-2/sm/install-7.webp",
              alt: "Connect to vehicle battery",
            },
            {
              lg: "/images/products/bluetti/charger-2/lg/install-8.webp",
              sm: "/images/products/bluetti/charger-2/sm/install-8.webp",
              alt: "Setup Charger 2 from BLUETTI app",
            },
          ]}
        /> */}
        <p>
          Because it uses screw terminals, it's important to check them
          occasionally to ensure they don't vibrate loose over time, which can
          be a fire hazard. I appreciate that BLUETTI doesn't use proprietary
          connectors, but it would be nice if they were fully
          vibration-resistant and included additional protections to prevent a
          user from wiring it incorrectly.
        </p>
        <h4>Cables</h4>
        <p>
          For the connection between Charger 2 and your vehicle’s battery,
          BLUETTI utilizes flexible, silicone-insulated 6AWG stranded wire
          encased in a durable braided sleeve. At over 16 feet, the battery
          cable is generously long, and its design allows for significantly
          easier routing compared to traditional cables with rigid PVC
          insulation. In contrast, EcoFlow uses standard PVC insulation and an
          overly tight braided wrap, resulting in a cumbersome cable that is
          much more difficult to maneuver.
        </p>
        <p>
          The PV and CHG cables are short and stubby, but can be easily extended
          with widely available{" "}
          <Link affiliate="https://amzn.to/3OdF2A1">
            solar extension cables
          </Link>
          . The BAT cable is more of a challenge since it doesn't have a
          built-in extension accessory. To extend it, you would need to build
          your own solution using what looks like{" "}
          <Link
            affiliate="https://amzn.to/41HLjHb"
            href="https://www.amazon.com/dp/B000NUYEKO/"
          >
            12 AWG wire
          </Link>{" "}
          and{" "}
          <Link
            affiliate="https://amzn.to/4cypu1G"
            href="https://www.amazon.com/dp/B08NWBRV4K/"
          >
            butt-splice connectors
          </Link>{" "}
          for the power lines, along with an Ethernet cable and{" "}
          <Link
            affiliate="https://amzn.to/42k5ACN"
            href="https://www.amazon.com/dp/B0D5ZHG8JX/"
          >
            coupler
          </Link>{" "}
          for the RJ45 data connection.
        </p>
        <Warning prefix="Warning">
          Pay close attention when installing the PV and CHG cables. It is easy
          to accidentally reverse them, which would result in reversed polarity
          to your power station and from your solar panels. This is especially
          confusing because one side of both cables is labeled "PV," while the
          other side is labeled as either "PV" or "CHG."
        </Warning>
        <p>
          Including the P090D cable seems unnecessary since many users won’t
          have a use for it. Given its limited compatibility with only a few
          BLUETTI models, selling it as an optional accessory would be a great
          way to reduce the unit's base price for everyone else.
        </p>
        <h4>Need an install guide for a Ford Transit?</h4>
        <ReviewItem href={namedRoutes.bluetti.altCharger.transit} />
      </Center>
      <Center>
        <SlugHeading tag="h3">Setup Videos</SlugHeading>
        <div class={style.videoSplit}>
          <div>
            <div>Install Guide</div>
            <YouTube id="2ejGheZQv3Y" />
          </div>
          <div>
            <div>App Setup</div>
            <YouTube id="dIXDqDwbods" />
          </div>
        </div>
      </Center>
      <Center>
        <SlugHeading tag="h2">App Control</SlugHeading>
        <p>
          The BLUETTI Charger 2 pairs with the BLUETTI app to allow for device
          monitoring and customization of default settings. The app is available
          on{" "}
          <Link
            external
            href="https://apps.apple.com/us/app/bluetti/id1550568336"
          >
            iOS
          </Link>{" "}
          and{" "}
          <Link
            external
            href="https://play.google.com/store/apps/details?id=net.poweroak.bluetticloud&pli=1"
          >
            Android
          </Link>
          . The following screenshots show all currently available options,
          ordered exactly as they appear in the settings menu.
        </p>

        <ImageSet
          size={6}
          maxShow={6}
          //shadow={true}
          border
          images={[
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260402_233451000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260402_233451000.webp",
              alt: "",
            },

            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260403_190423000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260403_190423000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260403_190506000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260403_190506000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260401_222233000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260401_222233000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260403_201526000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260403_201526000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260401_222239000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260401_222239000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260401_222245000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260401_222245000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260401_222250000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260401_222250000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260401_222257000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260401_222257000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260401_222304000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260401_222304000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260401_222309000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260401_222309000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260401_222314000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260401_222314000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260401_222332000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260401_222332000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260401_222346000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260401_222346000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260403_201754000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260403_201754000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260401_222357000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260401_222357000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260401_222402000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260401_222402000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260401_222407000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260401_222407000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260401_222412000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260401_222412000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260401_222420000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260401_222420000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260401_222428000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260401_222428000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260401_222431000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260401_222431000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260401_222435000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260401_222435000.webp",
              alt: "",
            },

            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260402_233458000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260402_233458000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205559000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205559000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205607000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205607000.webp",
              alt: "",
            },

            {
              lg: "/images/products/bluetti/charger-2/screenshots/lg/20260403_201841000.webp",
              sm: "/images/products/bluetti/charger-2/screenshots/sm/20260403_201841000.webp",
              alt: "",
            },
          ]}
        />

        <p class="caption-left">
          The app displays the device serial number on multiple main screens
          rather than a single settings page. This makes it far too easy to
          accidentally leak private hardware info and made capturing clean,
          shareable screenshots a chore.
        </p>

        <p>
          With a few small improvements, the app could be drastically improved:
        </p>
        <ul>
          <li>
            <strong>App design:</strong> I have designed user interfaces at
            Microsoft, and BLUETTI’s app would benefit from clearer
            organization. EcoFlow provides a good example of how to make common
            actions easy to reach without digging through settings menus for
            common items.
          </li>
          <li>
            <strong>Naming &amp; terminology:</strong> While the hardware is
            capable, the app’s naming conventions create unnecessary friction
            for the user. For instance, the hardware uses labels like BAT and
            CHG, but the app uses different terms like Parking Battery and PPS.
            Spelling out these acronyms and using consistent names across all
            platforms would make the system much more approachable. As it
            stands, the technical gap between the device and the app requires
            users to memorize a "secret code" for routine tasks, which increases
            the risk of accidental misconfiguration.
          </li>
          <li>
            <strong>In-App Guidance:</strong> While the existing tooltips are a
            great start for understanding complex settings, the app would
            benefit from more consistent coverage. Currently, several key
            features lack any explanation, which forces users to make
            assumptions about how a setting might impact their system. Adding
            tooltips for every toggle and including short, "how-to" video
            snippets in the guides section would go a long way in making the app
            feel like a complete resource for the user.
          </li>
          <li>
            <strong>Adjustable charging:</strong> In third-party mode, the app
            lets you adjust the charging rate, but this feature is missing for
            BLUETTI batteries on the CHG or BAT ports. This should be available
            for all connections and moved to the main screen for easy access.
            Even if an alternator supports 800W, users need the ability to dial
            it back on different trips to manage heat or efficiency without
            digging through settings.
          </li>
          <li>
            <strong>Reverse charging:</strong> This feature is currently buried
            too deep in the settings menu, making it inconvenient to toggle.
            Given that some users may want to activate this frequently to
            maintain a starter battery, it should be moved to the primary
            screen. This would make the overall user experience feel much more
            intuitive and responsive to daily needs.
          </li>
          <li>
            <strong>Vehicle Integration:</strong> The lack of CarPlay and
            Android Auto support is a missed opportunity for a device often used
            in van-life and overlanding setups where hands-free monitoring is
            important.
          </li>
        </ul>
      </Center>
      <Center>
        <SlugHeading tag="h2">Accessories</SlugHeading>

        <p>Accessory overview, read on for more details:</p>

        <ul class="none">
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/dc-hub-for-charger-2"
              img="/images/products/bluetti/charger-2/dc-hub.webp"
              title="DC Hub"
              titleSuffix="(not Hub D1 for Apex 200/300)"
              description={
                <Fragment>
                  700W Total DC Power
                  <br />
                  Ports: USB-C, USB-A, DC5521, Cigarette Lighter, and Anderson
                  Port
                </Fragment>
              }
              buttonText="BLUETTI"
              buttonIcon="Bluetti"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/charger-2-d-signal-wire"
              img="/images/products/bluetti/charger-2/d+-signal-wire.webp"
              title="D+ Signal Wire"
              description={
                <Fragment>Engine run detection (smart alternators)</Fragment>
              }
              buttonText="BLUETTI"
              buttonIcon="Bluetti"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/charger-2-bidirectional-power-cable?variant=48836358176987"
              img="/images/products/bluetti/charger-2/fridgepower-cable.webp"
              title="FridgePower - Bidirectional Power Cable"
              description={
                <Fragment>
                  Anderson SB50 port <br />
                  Compatible:{" "}
                  <Link
                    affiliateData={affiliateMerchants.bluetti}
                    href="https://www.bluettipower.com/products/fridgepower-battery-backup?id=6a2a69d42b71fb8ff494a178&variant=48862781210843"
                  >
                    FridgePower
                  </Link>
                  ,{" "}
                  <Link
                    affiliateData={affiliateMerchants.bluetti}
                    href="https://www.bluettipower.com/products/bluetti-bluecell-201?id=6a2a69d42b71fb8ff494a178&variant=48862781210843"
                  >
                    BlueCell 200
                  </Link>
                </Fragment>
              }
              buttonText="BLUETTI"
              buttonIcon="Bluetti"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/charger-2-bidirectional-power-cable?variant=48836358144219"
              img="/images/products/bluetti/charger-2/ac240-cable.webp"
              title="AC240 - Bidirectional Power Cable"
              description={
                <Fragment>
                  M28 Aviation port <br />
                  Compatible:{" "}
                  <Link
                    affiliateData={affiliateMerchants.bluetti}
                    href="https://www.bluettipower.com/products/ac240"
                  >
                    AC240
                  </Link>
                  ,{" "}
                  <Link
                    affiliateData={affiliateMerchants.bluetti}
                    href="https://www.bluettipower.com/products/b210"
                  >
                    B210
                  </Link>
                </Fragment>
              }
              buttonText="BLUETTI"
              buttonIcon="Bluetti"
            />
          </li>
        </ul>

        <div class={style.section}>
          <SlugHeading tag="h3">DC Hub</SlugHeading>
          <p>
            The DC Hub is a high-powered expansion unit providing a variety of
            DC ports. I applaud BLUETTI for including a 12V 50A output, as very
            few power station ecosystems offer this capacity, typically limiting
            users to 10A or 30A. Having this available is genuinely useful for
            higher power builds that require more substantial current.
          </p>

          <ul class="none">
            <li>
              <PhotoListItem
                affiliateData={affiliateMerchants.bluetti}
                href="https://www.bluettipower.com/products/dc-hub-for-charger-2"
                img="/images/products/bluetti/charger-2/dc-hub.webp"
                title="DC Hub"
                titleSuffix="(not Hub D1 for Apex 200/300)"
                description={
                  <Fragment>
                    700W Total DC Power
                    <br />
                    Ports: USB-C, USB-A, DC5521, Cigarette Lighter, and Anderson
                    Port
                  </Fragment>
                }
                buttonText="BLUETTI"
                buttonIcon="Bluetti"
              />
            </li>
          </ul>

          <p>
            The hub features a large mounting clip originally designed to mount
            on the handle of the Apex 300. The rear clip can be removed with a
            screwdriver, allowing the unit to be secured directly to a wall for
            a cleaner installation.
          </p>

          <ImageSet
            size={3}
            shadow
            images={[
              {
                lg: "/images/products/bluetti/dc-hub/1920/dc-hub_9547.webp",
                sm: "/images/products/bluetti/dc-hub/500/dc-hub_9547.webp",
                alt: "BLUETTI DC Hub",
              },
              {
                lg: "/images/products/bluetti/dc-hub/1920/dc-hub_9550.webp",
                sm: "/images/products/bluetti/dc-hub/500/dc-hub_9550.webp",
                alt: "BLUETTI DC Hub: back clip",
              },
              {
                lg: "/images/products/bluetti/dc-hub/1920/dc-hub_9548.webp",
                sm: "/images/products/bluetti/dc-hub/500/dc-hub_9548.webp",
                alt: "BLUETTI DC Hub: back clip removed",
              },
            ]}
          />

          <p>
            The DC Hub feels like a large and expensive workaround for what it
            provides. Ideally, this functionality would be integrated directly
            into the power stations themselves. Given its footprint and price
            point, the unit should have included dedicated space for a few
            circuits with blade fuses.
          </p>

          <p>
            The physical design is somewhat awkward; the power cable is quite
            thick, and the Anderson port would have made more sense on the same
            end as the cable to save space. While an Anderson SB50 connector is
            included, BLUETTI provides no crimping instructions or technical
            specifications. Furthermore, the included lugs only support 10–12
            AWG wire and do not accommodate the 6 AWG wire necessary for longer
            circuits carrying 50A loads.
          </p>

          <p>
            The app provides the ability to switch between 12V (50A) and 24V
            (25A) output. Beyond a simple toggle, the ability to set a precise
            voltage would be a major improvement, particularly for sensitive 12V
            electronics that may find the standard 13.5V output too high.
          </p>
          <p>
            The hub is not a standalone power solution and will not function
            with solar input alone. It requires either a running vehicle or an
            active BLUETTI device connected to the bidirectional port to provide
            power.
          </p>

          <p>
            If you need to extend the DC Hub cable that connects to Charger 2,
            you would need to build your own solution using{" "}
            <Link
              affiliate="https://amzn.to/4cum6ov"
              href="https://www.amazon.com/dp/B000NV2DPG/?psc=1"
            >
              14 AWG wire
            </Link>{" "}
            and{" "}
            <Link
              affiliate="https://amzn.to/4cypu1G"
              href="https://www.amazon.com/dp/B08NWBRV4K/"
            >
              butt-splice connectors
            </Link>{" "}
            for the power lines, along with an Ethernet cable and{" "}
            <Link
              affiliate="https://amzn.to/42k5ACN"
              href="https://www.amazon.com/dp/B0D5ZHG8JX/"
            >
              coupler
            </Link>{" "}
            for the RJ45 data connection.
          </p>
        </div>

        <div class={style.section}>
          <SlugHeading tag="h3">DIY D+ Cable</SlugHeading>
          <p>
            The official BLUETTI{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/charger-2-d-signal-wire"
            >
              D+ signal wire
            </Link>{" "}
            is now available for purchase, but you can easily create your own if
            necessary.
          </p>
          <ImageSet
            size={3}
            shadow
            images={[
              {
                lg: "/images/products/bluetti/charger-2/1920/charger-2_9561.webp",
                sm: "/images/products/bluetti/charger-2/500/charger-2_9561.webp",
                alt: "BLUETTI Charger 2 DIY D+ cable plugged in",
              },
              {
                lg: "/images/products/bluetti/charger-2/1920/charger-2_9564.webp",
                sm: "/images/products/bluetti/charger-2/500/charger-2_9564.webp",
                alt: "BLUETTI Charger 2 DIY D+ cable",
              },
              {
                lg: "/images/products/bluetti/charger-2/1920/charger-2_9559.webp",
                sm: "/images/products/bluetti/charger-2/500/charger-2_9559.webp",
                alt: "BLUETTI Charger 2 DIY D+ cable plugged in",
              },

              {
                lg: "/images/products/bluetti/charger-2/1920/charger-2_9743.webp",
                sm: "/images/products/bluetti/charger-2/500/charger-2_9743.webp",
                alt: "BLUETTI Charger 2 DIY D+ cable cutting",
              },
              {
                lg: "/images/products/bluetti/charger-2/1920/charger-2_9734.webp",
                sm: "/images/products/bluetti/charger-2/500/charger-2_9734.webp",
                alt: "BLUETTI Charger 2 DIY D+ cable comparison",
              },

              {
                lg: "/images/products/bluetti/charger-2/1920/charger-2_9740.webp",
                sm: "/images/products/bluetti/charger-2/500/charger-2_9740.webp",
                alt: "BLUETTI Charger 2 DIY D+ cable plugged in",
              },
            ]}
          />
          <p>To make one, you will need the following materials:</p>
          <ul>
            <li>
              <strong>
                <Link
                  affiliate="https://amzn.to/4bMxHyY"
                  href="https://www.amazon.com/dp/B01F4F5BPY/"
                >
                  5.08mm Pitch Pluggable Terminal
                </Link>
                :
              </strong>{" "}
              2-pin with screw terminals to interface with the charger.
            </li>
            <li>
              <strong>
                <Link
                  affiliate="https://amzn.to/4tjbiAB"
                  href="https://www.amazon.com/dp/B01LCEEEZY/"
                >
                  18 AWG Stranded Wire
                </Link>
              </strong>
              : BLUETTI uses{" "}
              <Link
                affiliate="https://amzn.to/3O3DAQw"
                href="https://www.amazon.com/dp/B01K4TLSWK/"
              >
                24 AWG
              </Link>
              , but I recommend 18 AWG for better durability in automotive
              environments.
            </li>
          </ul>
          <p>How to assemble:</p>
          <ol>
            <li>
              <strong>Trim the Terminal:</strong> Trim a small notch off the
              side of the green terminal with a knife so it clears the case and
              won't rattle loose.
            </li>
            <li>
              <strong>Wire the Plug:</strong> Strip a small section of wire,
              insert it into either pin of the terminal block, and tighten the
              screw clamp.
            </li>
          </ol>
          <p>
            Charger 2 shorts the terminal pins internally, meaning the signal
            cable can be connected to either of the two pins on the pluggable
            terminal to activate the charger. To make the Charger 2 truly
            "plug-and-play" for modern vehicles, BLUETTI should include this
            cable by default and make its status clear in the app interface.
          </p>
          <p>
            Depending on your vehicle’s setup, you will need a{" "}
            <Link
              affiliate="https://amzn.to/47XZnQi"
              href="https://www.amazon.com/s?k=fuse+tap"
            >
              fuse tap
            </Link>{" "}
            (ensure the type matches your vehicle's fuse box, e.g., ATO, Mini,
            or Micro2) or a{" "}
            <Link
              affiliate="https://amzn.to/4lZf9jM"
              href="https://www.amazon.com/s?k=ring+terminal+18awg"
            >
              ring terminal
            </Link>{" "}
            for connecting to a terminal stud (less common).
          </p>
          <p>
            <strong>Official D+ signal wire:</strong>
          </p>
          <PhotoListItem
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/products/charger-2-d-signal-wire"
            img="/images/products/bluetti/charger-2/d+-signal-wire.webp"
            title="D+ Signal Wire"
            description={
              <Fragment>Engine run detection (smart alternators)</Fragment>
            }
            buttonText="BLUETTI"
            buttonIcon="Bluetti"
          />
        </div>
        <div class={style.section}>
          <SlugHeading tag="h3">Bidirectional Compatibility</SlugHeading>
          <p>
            Bidirectional power flow is supported via the following cables with
            a compatible power station or battery:
          </p>

          <ul class="none">
            <li>
              <PhotoListItem
                affiliateData={affiliateMerchants.bluetti}
                img="/images/products/bluetti/charger-2/bidirectional-cable.webp"
                title="Bidirectional Power Cable"
                titleSuffix="(Included)"
                description={
                  <Fragment>
                    P090D port <br />
                    Batteries:{" "}
                    <Link
                      affiliateData={affiliateMerchants.bluetti}
                      href="https://www.bluettipower.com/products/b300k?id=673c0fb87410b96647e90c63&variant=45943077208283"
                    >
                      B300K
                    </Link>
                    ,{" "}
                    <Link
                      affiliateData={affiliateMerchants.bluetti}
                      href="https://www.bluettipower.com/products/b500k-expansion-battery?id=69118a354abc44066f33eb5b&variant=47914931847387"
                    >
                      B500K
                    </Link>
                    ,{" "}
                    <Link
                      affiliateData={affiliateMerchants.bluetti}
                      href="https://www.bluettipower.com/products/b230-battery-pack"
                    >
                      B230
                    </Link>
                    ,{" "}
                    <Link
                      affiliateData={affiliateMerchants.bluetti}
                      href="https://www.bluettipower.com/products/b300"
                    >
                      B300
                    </Link>{" "}
                    <br />
                    Power Stations:{" "}
                    <Link
                      affiliateData={affiliateMerchants.bluetti}
                      href="https://www.bluettipower.com/products/apex-300-home-battery-backup?id=68804e7e63e18b529ef2604a&variant=47351562174683"
                    >
                      Apex 300
                    </Link>
                    ,{" "}
                    <Link
                      affiliateData={affiliateMerchants.bluetti}
                      href="https://www.bluettipower.com/products/ac200l?id=673d48af5295be4bd97b0955&variant=46352232349915"
                    >
                      AC200L
                    </Link>
                    ,{" "}
                    <Link
                      affiliateData={affiliateMerchants.bluetti}
                      href="https://www.bluettipower.com/products/ac200max-power-station?variant=44272419995867"
                    >
                      AC200MAX
                    </Link>
                  </Fragment>
                }
                buttonText="BLUETTI"
                buttonIcon="Bluetti"
              />
            </li>
            <li>
              <PhotoListItem
                affiliateData={affiliateMerchants.bluetti}
                href="https://www.bluettipower.com/products/charger-2-bidirectional-power-cable?variant=48836358176987"
                img="/images/products/bluetti/charger-2/fridgepower-cable.webp"
                title="FridgePower - Bidirectional Power Cable"
                description={
                  <Fragment>
                    Anderson SB50 port <br />
                    Compatible:{" "}
                    <Link
                      affiliateData={affiliateMerchants.bluetti}
                      href="https://www.bluettipower.com/products/fridgepower-battery-backup?id=6a2a69d42b71fb8ff494a178&variant=48862781210843"
                    >
                      FridgePower
                    </Link>
                    ,{" "}
                    <Link
                      affiliateData={affiliateMerchants.bluetti}
                      href="https://www.bluettipower.com/products/bluetti-bluecell-201?id=6a2a69d42b71fb8ff494a178&variant=48862781210843"
                    >
                      BlueCell 200
                    </Link>
                  </Fragment>
                }
                buttonText="BLUETTI"
                buttonIcon="Bluetti"
              />
            </li>
            <li>
              <PhotoListItem
                affiliateData={affiliateMerchants.bluetti}
                href="https://www.bluettipower.com/products/charger-2-bidirectional-power-cable?variant=48836358144219"
                img="/images/products/bluetti/charger-2/ac240-cable.webp"
                title="AC240 - Bidirectional Power Cable"
                description={
                  <Fragment>
                    M28 Aviation port <br />
                    Compatible:{" "}
                    <Link
                      affiliateData={affiliateMerchants.bluetti}
                      href="https://www.bluettipower.com/products/ac240"
                    >
                      AC240
                    </Link>
                    ,{" "}
                    <Link
                      affiliateData={affiliateMerchants.bluetti}
                      href="https://www.bluettipower.com/products/b210"
                    >
                      B210
                    </Link>
                  </Fragment>
                }
                buttonText="BLUETTI"
                buttonIcon="Bluetti"
              />
            </li>
          </ul>

          <p class="caption-left">
            A cable for the{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/bluetti-b300s-expansion-battery"
            >
              B300S
            </Link>{" "}
            is mentioned in the original manual but is not yet available for
            purchase.
          </p>

          <ReviewItem href={namedRoutes.bluetti.ac200l} />
        </div>

        <Accordion
          items={[
            {
              heading: "Included Accessories",
              content: (
                <Fragment>
                  <h4>Standard Package</h4>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Car Battery Cables:</strong> 16.4 ft (5 m), 6 AWG,
                      with 75A fuse
                    </li>
                    <li>
                      <strong>MC4 Input Cable:</strong> 7.87 in (200 mm)
                    </li>
                    <li>
                      <strong>MC4 Output Cable:</strong> 7.87 in (200 mm)
                    </li>
                    <li>
                      <strong>Solar Charging Cable (MC4 to XT60):</strong> 7.87
                      in (200 mm)
                    </li>
                    <li>
                      <strong>Bidirectional Power Cable (P090D):</strong> 4.92
                      ft (1.5 m), for{" "}
                      <Link
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/apex-300-home-battery-backup?id=68804e7e63e18b529ef2604a&variant=47351562174683"
                      >
                        Apex 300
                      </Link>
                      ,{" "}
                      <Link
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/ac200l?id=673d48af5295be4bd97b0955&variant=46352232349915"
                      >
                        AC200L
                      </Link>
                      ,{" "}
                      <Link
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/ac200max-power-station?variant=44272419995867"
                      >
                        AC200MAX
                      </Link>
                      ,{" "}
                      <Link
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/b300k?id=673c0fb87410b96647e90c63&variant=45943077208283"
                      >
                        B300K
                      </Link>
                      ,{" "}
                      <Link
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/b500k-expansion-battery?id=69118a354abc44066f33eb5b&variant=47914931847387"
                      >
                        B500K
                      </Link>
                      ,{" "}
                      <Link
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/b230-battery-pack"
                      >
                        B230
                      </Link>
                      ,{" "}
                      <Link
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/b300"
                      >
                        B300
                      </Link>
                    </li>
                  </ul>
                  <hr />

                  <h4>Charger 1 Upgrade Package</h4>
                  <p>Includes all standard items except: Car Battery Cables</p>
                </Fragment>
              ),
            },
            {
              heading: "Optional Accessories",
              content: (
                <Fragment>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>
                        <Link
                          affiliateData={affiliateMerchants.bluetti}
                          href="https://www.bluettipower.com/products/dc-hub-for-charger-2"
                        >
                          DC Hub
                        </Link>
                        :
                      </strong>{" "}
                      for additional DC ports and 50A DC output
                    </li>
                    <li>
                      <strong>
                        <Link
                          affiliateData={affiliateMerchants.bluetti}
                          href="https://www.bluettipower.com/products/charger-2-d-signal-wire"
                        >
                          D+ Signal Wire
                        </Link>
                        :
                      </strong>{" "}
                      16.4 ft (5 m), for smart alternator
                    </li>
                    {/* <li>
                      <strong>Car Battery Cables:</strong> 26.25 ft (8 m), 6
                      AWG, with 75 A fuse (Availability TBD)
                    </li> */}
                    <li>
                      <strong>
                        <Link
                          affiliateData={affiliateMerchants.bluetti}
                          href="https://www.bluettipower.com/products/charger-2-bidirectional-power-cable?variant=48836358176987"
                        >
                          Bidirectional Power Cable
                        </Link>{" "}
                        (Anderson SB50):
                      </strong>{" "}
                      4.92 ft (1.5 m), for{" "}
                      <Link
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/fridgepower-battery-backup?id=6a2a69d42b71fb8ff494a178&variant=48862781210843"
                      >
                        FridgePower
                      </Link>
                      ,{" "}
                      <Link
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/bluetti-bluecell-201?id=6a2a69d42b71fb8ff494a178&variant=48862781210843"
                      >
                        BlueCell 200
                      </Link>
                    </li>
                    <li>
                      <strong>
                        <Link
                          affiliateData={affiliateMerchants.bluetti}
                          href="https://www.bluettipower.com/products/charger-2-bidirectional-power-cable?variant=48836358144219"
                        >
                          Bidirectional Power Cable
                        </Link>{" "}
                        (M28 Aviation):
                      </strong>{" "}
                      4.92 ft (1.5 m), for{" "}
                      <Link
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/ac240"
                      >
                        AC240
                      </Link>
                      ,{" "}
                      <Link
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/b210"
                      >
                        B210
                      </Link>
                    </li>
                    <li>
                      <strong>Bidirectional Power Cable:</strong> 4.92 ft (1.5
                      m), for{" "}
                      <Link
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/bluetti-b300s-expansion-battery"
                      >
                        B300S
                      </Link>{" "}
                      (Availability TBD)
                    </li>
                  </ul>
                </Fragment>
              ),
            },
          ]}
        />
      </Center>
      {/* <Center>
        <SlugHeading tag="h2">Areas for Improvement</SlugHeading>
        <p>
          Hoping this feedback can help improve future revisions and updates:
        </p>
        <ul>
          <li>
            <strong>Provide Performance Data:</strong> Right now, users do not
            get detailed efficiency curves or temperature‑related performance
            data. BLUETTI could publish more complete datasheets so advanced
            users can better predict real‑world performance, especially in hot
            or enclosed installations.
          </li>
          <li>
            <strong>Support experience:</strong> While the support team answers
            the phone very quickly, the agents often lacked detailed knowledge
            about the Charger 2. Improving technical training for the staff
            would ensure that their great response times are matched by helpful,
            accurate answers for users.
          </li>
        </ul>
      </Center> */}
      <Center>
        <SlugHeading tag="h2">My Verdict</SlugHeading>
        <p>
          The <strong>BLUETTI Charger 2</strong> is a high-speed DC-DC
          alternator charger that delivers up to <strong>1,200W</strong> of
          combined vehicle and solar input. It supports bidirectional charging
          to maintain both connected power stations and the vehicle's starter
          battery, while an integrated{" "}
          <Link
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/products/dc-hub-for-charger-2"
          >
            DC Hub
          </Link>{" "}
          port offers comprehensive DC power distribution for off-grid systems.
        </p>
        <p>
          Some features rely on specific hardware and come with system
          limitations that are important to keep in mind. Overall, it’s a
          capable and competitive alternator‑charger option, with real‑world
          performance that depends on the equipment you pair it with. Be aware
          of a few current firmware issues with the Charger 2 and DC Hub that
          impact some setups.
        </p>
        {/* <p>
          <strong>The Bottom Line:</strong> If you are already in the BLUETTI
          ecosystem and want the fastest charge times, this is a strong upgrade
          to consider.
        </p> */}
        <p>
          <strong>The Bottom Line:</strong> If you are already in the BLUETTI
          ecosystem and want the fastest charge times, this is a strong hardware
          upgrade, provided you are aware of the current firmware limitations.
        </p>

        <BluettiCouponCode
          mini
          href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
          buttonText="Buy from BLUETTI"
        />

        <p>
          Not sure it’s the best fit? My{" "}
          <Link href={namedRoutes.alternatorChargers}>
            alternator charger guide
          </Link>{" "}
          covers all the popular brands.
        </p>

        <ButtonList>
          <Button
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
            icon="Bluetti"
          >
            Buy from BLUETTI
          </Button>
          <Button
            affiliate="https://amzn.to/4q6J9LD"
            href="https://www.amazon.com/dp/B0FS7C11QG/"
            secondary
            icon="Amazon"
          >
            Buy from Amazon
          </Button>
        </ButtonList>

        <p class="caption-left">
          This page will be updated as new information becomes available.
        </p>

        <Tags tags={headerInfo.tags} brands={headerInfo.brands} />
      </Center>
      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.alternatorChargers} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.bluetti.altCharger.transit} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.inventory.vanlife} />
          </li>
        </ul>
      </Center>
      <Questions />
      <Center>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
      <StickyButtons
        title="BLUETTI Charger 2"
        description={
          <Fragment>
            ${price.current}{" "}
            {price.current < price.msrp ? <s>${price.msrp}</s> : null}
          </Fragment>
        }
      >
        <Button
          affiliateData={affiliateMerchants.bluetti}
          href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
          icon="Bluetti"
        >
          View on BLUETTI
        </Button>
        <Button
          affiliate="https://amzn.to/4q6J9LD"
          href="https://www.amazon.com/dp/B0FS7C11QG/"
          secondary
          icon="Amazon"
        >
          View on Amazon
        </Button>
      </StickyButtons>
    </Editorial>
  </div>
);

export default Page;
